import { useEffect, useRef, useState } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { Calendar, CheckCircle, Monitor } from 'lucide-react';

const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  const stats = [
    { 
      value: 5, 
      label: "Years of Experience",
      subtext: "Developing Web & Mobile Software",
      icon: <Calendar className="h-6 w-6" />
    },
    { 
      value: 10, 
      label: "Projects Delivered",
      subtext: "Full-Stack & Client Applications",
      icon: <CheckCircle className="h-6 w-6" />
    },
    { 
      value: 20, 
      label: "Apps & Sites Built",
      subtext: "Cross-Platform & Web Portals",
      icon: <Monitor className="h-6 w-6" />
    }
  ];

  return (
    <section 
      ref={ref}
      className="w-full py-20 bg-gradient-to-b from-gray-50/50 via-white to-gray-50/50 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950 relative overflow-hidden"
    >
      {/* Decorative background elements */}
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-200/40 dark:bg-indigo-900/10 rounded-full blur-3xl opacity-35"></div>
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200/40 dark:bg-purple-900/10 rounded-full blur-3xl opacity-35"></div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-gray-900 dark:text-white mb-3">
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent">
              Key Track Record & Milestones
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
            Consistently delivering high quality user interfaces, seamless REST/GraphQL integration, and fast performance.
          </p>
        </motion.div>
        
        <motion.div 
          initial="hidden"
          animate={controls}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
              }
            }
          }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { y: 30, opacity: 0 },
                visible: {
                  y: 0,
                  opacity: 1,
                  transition: {
                    type: "spring",
                    damping: 15,
                    stiffness: 100
                  }
                }
              }}
              whileHover={{ 
                y: -8,
                boxShadow: "0 20px 30px -10px rgba(99, 102, 241, 0.15)"
              }}
              className="bg-white/80 dark:bg-gray-900/60 rounded-3xl p-8 border border-gray-200/80 dark:border-gray-800 relative overflow-hidden transition-all duration-300 glass-card"
            >
              {/* Top Accent Gradient Border */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-80"></div>
              
              <div className="relative z-10 flex flex-col items-center text-center">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ 
                    type: "spring",
                    delay: 0.3 + index * 0.15,
                    stiffness: 150
                  }}
                  className="w-14 h-14 flex items-center justify-center rounded-2xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 mb-5 border border-indigo-200/60 dark:border-indigo-800/60 shadow-sm"
                >
                  {stat.icon}
                </motion.div>
                
                <div className="flex items-baseline mb-2 justify-center">
                  <span className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent mr-1">
                    +
                  </span>
                  <CountUp end={stat.value} />
                </div>
                
                <h3 className="text-gray-900 dark:text-white font-bold font-heading text-lg mb-1">
                  {stat.label}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-xs">
                  {stat.subtext}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// CountUp Component
const CountUp = ({ end }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const frameRef = useRef(null);
  
  useEffect(() => {
    const duration = 1500;
    const startTime = Date.now();
    
    const updateCount = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      countRef.current = easeOutQuart * end;
      setCount(Math.floor(countRef.current));
      
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(updateCount);
      } else {
        setCount(end);
      }
    };
    
    frameRef.current = requestAnimationFrame(updateCount);
    
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [end]);

  return (
    <span className="text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 dark:from-indigo-400 dark:via-purple-400 dark:to-pink-400 bg-clip-text text-transparent inline-block font-heading">
      {count}
    </span>
  );
};

export default StatsSection;
